<template>
  <div v-loading="loading">
    <!-- Top Bar: Nomination ID on left, buttons on right -->
    <div class="flex justify-between items-center py-3 px-2">
      <div class="text-sm text-gray-600 font-bold">
        Nomination: {{ nominationId }}
      </div>
      <div class="flex gap-2">
        <el-button
          v-if="isEditable"
          size="mini"
          type="primary"
          style="font-size: 12px"
          @click="handleSave"
        >
          Save
        </el-button>
        <el-button plain size="mini" type="primary" style="font-size: 12px" @click="handleCancel">
          Close
        </el-button>
      </div>
    </div>

    <!-- Header -->
    <div class="content rounded-t">
      <div class="header-highlight" style="display: flex; align-items: center; justify-content: center;">
        <div>
          <span class="header-text">High Variance Investigation Checklist (Bunker Barge)</span>
        </div>
      </div>
    </div>

    <!-- Group 1: Checks on Shore Side -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">CHECKS ON SHORE SIDE</div>
      <el-table :data="mainItems" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="1.0" width="70" align="center">
          <template v-slot="scope">
            {{ scope.row.number }}
          </template>
        </el-table-column>
  
        <el-table-column label="Description" prop="description" width="420">
          <template v-slot="scope">
            <div class="description-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column
          label="Findings"
          cell-class-name="auto-height-cell"
        >
          <template v-slot="scope">
            <div class="findings-row">
              <div
                v-for="field in scope.row.fields"
                :key="field.key"
                class="findings-field"
              >
                <label class="field-label">{{ field.label }}</label>
                <el-input
                  v-model="scope.row.data[field.key]"
                  type="textarea"
                  :autosize="{ minRows: 1, maxRows: 6 }"
                  size="mini"
                  :disabled="!isEditable"
                  class="auto-height-input"
                />
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Group 2: Vessel Arrival & Calibration Checklist -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">
        <div class="flex flex-col">
          <span>CHECKS ON SHIP'S SIDE</span>
        </div>
          <div class="mt-2">
            <el-button
              type="primary"
              size="mini"
              @click="checkAllAsYes"
              :disabled="!isEditable"
            >
              Check all as Yes
            </el-button>
          </div>
      </div>
      <el-table :data="checklistItems" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="2.0" width="70" align="center">
          <template v-slot="scope">
            {{ scope.row.number }}
          </template>
        </el-table-column>
        <el-table-column label="Description" prop="description" width="420">
          <template v-slot="scope">
            <div class="description-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Findings" width="160">
          <template v-slot="scope">
            <el-radio-group
              v-model="scope.row.data.findings"
              :disabled="!isEditable"
              size="mini"
            >
              <el-radio-button label="Yes">Yes</el-radio-button>
              <el-radio-button label="No">No</el-radio-button>
            </el-radio-group>
          </template>
        </el-table-column>
        <el-table-column label="Remarks" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              v-model="scope.row.data.remarks"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              size="mini"
              :disabled="!isEditable"
              class="auto-height-input checklist-input"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Group 3: Variances -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">VARIANCES</div>
      <el-table :data="varianceItems" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="3.0" width="70" align="center">
          <template v-slot="scope">
            {{ scope.row.number }}
          </template>
        </el-table-column>
        <el-table-column label="Variances" prop="description" width="420">
          <template v-slot="scope">
            <div class="description-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Gross" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              v-model="scope.row.data.gross"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              size="mini"
              :disabled="!isEditable"
              class="auto-height-input variance-input"
            />
          </template>
        </el-table-column>
        <el-table-column label="Net" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              v-model="scope.row.data.net"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              size="mini"
              :disabled="!isEditable"
              class="auto-height-input variance-input"
            />
          </template>
        </el-table-column>
        <el-table-column label="MT" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              v-model="scope.row.data.mt"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              size="mini"
              :disabled="!isEditable"
              class="auto-height-input variance-input"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Group 4: Documents Required for Investigation -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">DOCUMENTS REQUIRED FOR INVESTIGATION</div>
      <el-table :data="documentItems" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="4.0" width="70" align="center">
          <template v-slot="scope">
            {{ scope.row.number }}
          </template>
        </el-table-column>
        <el-table-column label="Document" prop="description" width="300">
          <template v-slot="scope">
            <div class="description-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Shore">
          <template v-slot="scope">
            <el-radio-group
              v-model="scope.row.data.requiredShore"
              :disabled="!isEditable"
              size="mini"
            >
              <el-radio label="Required">Required</el-radio>
              <el-radio label="Not Required">Not Required</el-radio>
            </el-radio-group>
          </template>
        </el-table-column>
        <el-table-column label="Ship">
          <template v-slot="scope">
            <el-radio-group
              v-model="scope.row.data.requiredShip"
              :disabled="!isEditable"
              size="mini"
            >
              <el-radio label="Required">Required</el-radio>
              <el-radio label="Not Required">Not Required</el-radio>
            </el-radio-group>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Group 5: Remarks -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">Remarks</div>
      <el-table :data="remarkItems" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="5.0" width="70" align="center">
          <template v-slot="scope">
            {{ scope.row.number }}
          </template>
        </el-table-column>
        <el-table-column label="Remarks" prop="description" width="420">
          <template v-slot="scope">
            <div class="description-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Shore side" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              v-model="scope.row.data.shoreSide"
              :disabled="!isEditable"
              size="mini"
              class="auto-height-input"
            />
          </template>
        </el-table-column>
        <el-table-column label="Ship side" cell-class-name="auto-height-cell">
          <template v-slot="scope">
            <el-input
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 6 }"
              v-model="scope.row.data.shipSide"
              :disabled="!isEditable"
              size="mini"
              class="auto-height-input"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- User Table -->
    <el-card class="box-card mt-4">
      <div slot="header" class="card-header">Investigation Conducted By</div>
      <el-table :data="formData.userList" border class="styled-checklist-table" row-class-name="styled-row">
        <el-table-column label="Name">
          <template v-slot="scope">
            <el-input v-model="scope.row.name" :disabled="!isEditable" size="mini" />
          </template>
        </el-table-column>
        <el-table-column label="Designation">
          <template v-slot="scope">
            <el-input v-model="scope.row.designation" :disabled=" !isEditable" size="mini" />
          </template>
        </el-table-column>
        <el-table-column label="Date">
          <template v-slot="scope">
            <el-date-picker
              v-model="scope.row.date"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :disabled=" !isEditable"
              size="mini"
              style="width: 100%"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>


<script>
import { getHighVarianceReport, saveOrUpdateHighVarianceReport } from '@/services/form.js';
import { getUserInfo } from '@/services/user';

export default {
  name: 'HighVarianceReport',
  props: {
    nominationId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      // We only need a combined list plus userList.
      formData: {
        formType: 'complete_01',
        list: [],
        userList: []
      },
      isCompleted: false
    };
  },
  computed: {
    isEditable() {
      const currentUserId = this.$store.state.userInfo?.userId;
      const leaderRow = this.formData.userList[0];
      return !this.isCompleted || (leaderRow.userId != null && leaderRow.userId === currentUserId);
    },
    // Filter the combined list by group based on number
    mainItems() {
      return this.formData.list.filter(item => item.number.startsWith('1.'));
    },
    checklistItems() {
      return this.formData.list.filter(item => item.number.startsWith('2.'));
    },
    varianceItems() {
      return this.formData.list.filter(item => item.number.startsWith('3.'));
    },
    documentItems() {
      return this.formData.list.filter(item => item.number.startsWith('4.'));
    },
    remarkItems() {
      return this.formData.list.filter(item => item.number.startsWith('5.'));
    }
  },
  methods: {
    initializeBlankForm() {
      // Combine all your form items (except userList) into one array.
      this.formData.list = [
        // Group 1: Checks on Shore Side
        {
          number: '1.1',
          description: '',
          fields: [
            { key: 'systemValue', label: 'System:' },
            { key: 'pdMeterNo', label: 'PD Meter No.:' }
          ],
          data: {}
        },
        {
          number: '1.1.1',
          description: '',
          fields: [
            { key: 'customer', label: 'Customer:' },
            { key: 'bargeName', label: 'Barge Name:' },
          ],
          data: {}
        },
        {
          number: '1.1.2',
          description: '',
          fields: [
            { key: 'lastBargeName', label: 'Last Barge Name:' },
            { key: 'variance', label: 'Variance:' },
            { key: 'pdMeterNo', label: 'PD Meter No.:' }
          ],
          data: {}
        },
        {
          number: '1.2',
          description: '',
          fields: [
            { key: 'og', label: 'OG:' },
            { key: 'cg', label: 'C/G:' }
          ],
          data: {}
        },
        {
          number: '1.3',
          description: '',
          fields: [
            { key: 'previousClosingLevel', label: 'Previous closing Level:' },
            { key: 'reasonLevelDifferent', label: 'Indicate the reason when level is different:' }
          ],
          data: {}
        },
        {
          number: '1.4',
          description: '',
          fields: [
            { key: 'findings', label: '' }
          ],
          data: {}
        },
        {
          number: '1.5',
          description: '',
          fields: [
            { key: 'previousClosingReading', label: 'Previous Totaliser Closing Reading:' },
            { key: 'currentOpeningReading', label: 'Current Totaliser Opening Reading:' }
          ],
          data: {}
        },
        {
          number: '1.6',
          description: '',
          fields: [
            { key: 'tank', label: 'Tank:' },
            { key: 'jetty', label: 'Jetty:' },
            { key: 'psvm', label: 'PSVM:' }
          ],
          data: {}
        },
        {
          number: '1.7',
          description: '',
          fields: [
            { key: 'beforeOrAfter', label: 'Before OR After:' },
            { key: 'displacementQuantity', label: 'Displacement Quantity:' },
            { key: 'quantityAdded', label: 'Quantity Added:' }
          ],
          data: {}
        },
        {
          number: '1.8',
          description: '',
          fields: [
            { key: 'nonActiveTanks', label: 'Non-active Tanks:' },
            { key: 'slopTanks', label: 'Slop Tanks:' }
          ],
          data: {}
        },
        {
          number: '1.9',
          description: '',
          fields: [
            { key: 'waterAccumulated', label: 'Water accumulated on roof deck:' },
            { key: 'openOrClosingGauge', label: 'Opening / closing gauge printed during rain:' }
          ],
          data: {}
        },
        {
          number: '1.10',
          description: '',
          fields: [
            { key: 'movOpenCorrectly', label: 'MOV Open Correctly:' },
            { key: 'manualOpenCorrectly', label: 'Manual Open Correctly:' },
            { key: 'happen', label: 'If No, what happen:' }
          ],
          data: {}
        },
        {
          number: '1.12',
          description: '',
          fields: [
            { key: 'shoreTankTemp', label: 'Shore Tank Temp:' },
            { key: 'jettyTemp', label: 'Jetty Temp:' },
            { key: 'shipTemp', label: "Ship's Temp:" }
          ],
          data: {}
        },
        {
          number: '1.13',
          description: '',
          fields: [
            { key: 'checked', label: '' }
          ],
          data: {}
        },
        {
          number: '1.14',
          description: '',
          fields: [
            { key: 'outerOrInnerBerths', label: 'Outer / Inner berths:' },
            { key: 'itt', label: 'ITT:' }
          ],
          data: {}
        },

        // Group 2: Vessel Arrival & Calibration Checklist
        {
          number: '2.1a',
          description: 'Is there any ROB (Remainder on-board) and verified? If yes, give detail.',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.1b',
          description: 'Is loading on top of ROB? If yes, LOP issued?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.1c',
          description: 'Is ROB supported by certified documents verifying for comparison of quantity and quality? If no, indicate reason(s)?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.1d',
          description: 'Is the vessel fully loaded or in slack condition?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.2',
          description: 'What is the maximum capacity of the vessel\'s cargo tanks at 98%?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.3a',
          description: 'Valid document verified?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.3b',
          description: 'Baring the vessel name and stamped, used to determine the cargo quantity onboard?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.4',
          description: 'Is the vessel equipped with a certified gauging tape and thermometer at all times and used for the measurement of her cargo tanks?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.4a',
          description: 'Is verification with SHORE gauging tape and thermometer (TP-?) conducted?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.5',
          description: 'Is the Reference Height (RH2) of each tank onboard verified and compared physically to be the same?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.6',
          description: 'Are pipelines contents onboard added to the measured tanks’ volumes?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.7',
          description: 'Any surveyor engaged for operations?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.8',
          description: 'To use both ullage & sounding method to conduct checks on ship’s tank level / quantity.',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.9',
          description: 'Check draft - apply correct trim correction, compare previous records',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.10',
          description: 'Is the Barge on “EVEN KEEL” after loaded? If not, barge to be on even keel and conduct re-ullage to prevent trim correction apply wrongly',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.11',
          description: 'Any verification conducted on “OBQ-ROB” by loading 500KGL for comparison?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },
        {
          number: '2.12',
          description: 'Is the MFM certify and with a valid certificate? Is the copy of calibration/verification certificate collected?',
          fields: [
            { key: 'findings', label: 'Findings' },
            { key: 'remarks', label: 'Remarks' }
          ],
          data: {}
        },

        // Group 3: Variances
        {
          number: '3.1',
          description: 'Shore PD Meter figure',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },
        {
          number: '3.2',
          description: 'Ship sounding figure (Ship do not have ullage calibration)',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },
        {
          number: '3.3',
          description: 'Variance - PD meter & ship\'s sounding (Quantity / Percentage %)',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },
        {
          number: '3.4',
          description: 'Ship\'s MFM figure',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },
        {
          number: '3.5',
          description: 'Variance - PD meter & MFM (Quantity / Percentage %)',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },
        {
          number: '3.6',
          description: 'Variance - MFM & ship\'s sounding (Quantity / Percentage %)',
          fields: [
            { key: 'gross', label: 'Gross' },
            { key: 'net', label: 'Net' },
            { key: 'mt', label: 'MT' }
          ],
          data: {}
        },

        // Group 4: Documents Required for Investigation
        {
          number: '4.1',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.2',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.3',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.4',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.5',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.6',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.7',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },
        {
          number: '4.8',
          description: '',
          fields: [
            { key: 'requiredShore', label: 'Shore' },
            { key: 'requiredShip', label: 'Ship' }
          ],
          data: {}
        },

        // Group 5: Remarks
        {
          number: '5.1',
          description: 'The high variance investigation was conducted immediately after it was reported by the technician on duty at Jetty that attended to above mentioned bunker barge. The following were the checks conducted & the summary of findings.',
          fields: [
            { key: 'shoreSide', label: 'Shore Side' },
            { key: 'shipSide', label: 'Ship Side' }
          ],
          data: {}
        }
      ];

      // Initialize the userList separately
      this.formData.userList = [
        { name: '', designation: '', date: '' },
        { name: '', designation: '', date: '' },
        { name: '', designation: '', date: '' },
        { name: '', designation: '', date: '' },
        { name: '', designation: '', date: '' }
      ];
    },
    async fetchReport() {
      this.loading = true;
      try {
        const res = await getHighVarianceReport({ nominationId: this.nominationId });
        const backendData = res.data;

        if (backendData && Array.isArray(backendData.list) && backendData.list.length > 0) {
          this.initializeBlankForm();

          const formMap = {};
          this.formData.list.forEach(item => {
            formMap[item.number] = { ...item, data: {} };
          });
          backendData.list.forEach(item => {
            const number = item.formItem;
            if (formMap[number]) {
              formMap[number].description = item.formDescription || formMap[number].description;
              formMap[number].data = {};
              formMap[number].fields.forEach(field => {
                formMap[number].data[field.key] =
                  (item[field.key] !== null && item[field.key] !== undefined)
                    ? item[field.key]
                    : "";
              });
            }
          });
          this.formData.list = Object.values(formMap);
          
          let fetchedUsers = [];
          if (backendData.userList && backendData.userList.length > 0) {
            // Keep the first saved user as-is, permanently locked
            fetchedUsers = backendData.userList.map((user, idx) => ({
              userId:   parseInt(user.updateBy, 10),
              name: user.name || '',
              designation: user.designation || '',
              date: user.date || '',
              locked: idx === 0
            }));
          } else {
            // Only when userList is empty (new form), set first user to current user
            const userInfo = await getUserInfo();
            const currentUserId   = userInfo.user.userId;
            const currentUserName = userInfo?.user.nickName;
            const today = new Date().toISOString().split("T")[0];

            fetchedUsers.push({
              userId: currentUserId,
              name: currentUserName,
              designation: "Leader",
              date: today,
              locked: true
            });
          }

          // Fill remaining rows if less than 5
          while (fetchedUsers.length < 5) {
            fetchedUsers.push({ name: '', designation: '', date: '', locked: false });
          }
          this.formData.userList = fetchedUsers;

          this.isCompleted = true;
        } else {
          this.initializeBlankForm();
        }
      } catch (err) {
        this.$message.error("Failed to load report.");
        this.initializeBlankForm();
      } finally {
        this.loading = false;
      }
    },
    async handleSave() {
      const flatList = this.formData.list.map((item, index) => {
        const base = {
          nominationId: this.nominationId,
          formId: 254 + index,
          formItem: item.number,
          formDescription: item.description,
          orderIndex: index + 1
        };
        item.fields.forEach(field => {
          base[field.key] = item.data[field.key] || "";
        });
        return base;
      });

      const userListPayload = this.formData.userList.map(user => ({
        nominationId: this.nominationId,
        name: user.name,
        designation: user.designation,
        date: user.date
      }));

      await saveOrUpdateHighVarianceReport({
        nominationId: this.nominationId,
        formType: this.formData.formType,
        list: flatList,
        userList: userListPayload
      });

      this.isCompleted = true;
      this.$message.success("Saved successfully!");
      await this.fetchReport();

      this.$nextTick(() => {
        const top = this.$el.querySelector('.box-card')?.offsetTop || 0;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    },
    handleCancel() {
      this.$router.go(0);
    },
    isUserRowLocked(user) {
      return Boolean(user.name || user.designation || user.date);
    },
    checkAllAsYes() {
      this.formData.list.forEach(item => {
        if (item.number.startsWith("2.") && item.fields.some(f => f.key === "findings")) {
          item.data.findings = "Yes";
        }
      });
    },
  },
  mounted() {
    this.fetchReport();
  }
};
</script>

<style scoped>
.box-card {
  margin-top: 0;
  margin-bottom: 0;
  border-bottom: none;
  border-radius: 0;
}
.box-card:last-child {
  border-bottom: 1px solid #dcdfe6;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.box-card:first-of-type {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.item {
  padding: 10px;
  font-weight: 600;
  font-size: 14px;
  background-color: #203447;
  color: white;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.circle {
  width: 14px;
  height: 14px;
  border: 2px solid #CFCFCF;
  border-radius: 50%;
  margin-left: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.styled-checklist-table .el-table__cell {
  vertical-align: middle;
}
.description-cell {
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 1.5;
  white-space: pre-wrap;
  padding: 10px;
}
.findings-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding-left: 0px;
  overflow-x: auto;
}
.findings-input {
  min-width: 140px;
  flex: 0 0 auto;
}
.styled-checklist-table .el-table__cell {
  vertical-align: middle;
}
.description-cell {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 10px;
  font-size: 13px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}
.findings-row {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
}
::v-deep(.el-card__header) {
  font-weight: 600;
  color: #303133;
  padding: 12px 20px;
  border-bottom: 1px solid #e4e7ed;
  font-size: 16px;
  font-weight: 600;
}
:deep(.el-card .el-table .el-input__inner) {
  height: 40px !important;
  font-size: 14px !important;
}

.findings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}

.findings-field {
  flex: 1 1 0;
  min-width: 160px;
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 4px;
  font-weight: 500;
}

::v-deep(.el-table__body-wrapper td) {
  border-bottom: 1px solid #dcdfe6 !important;
}

::v-deep(.el-table__body-wrapper tr:last-child td) {
  border-bottom: none !important;
}

::v-deep(.el-table__header th) {
  background-color: #f0f8fe !important;
  color: rgba(0, 0, 0, 0.674) !important;
}

.header-highlight {
  background-color: #fff;
  padding: 10px;
  text-align: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom: 6px solid #3b9afa;
}

.header-text {
  font-size: 16px;
  color: #409EFF;
  font-weight: 600;
}

.check-all-button {
  margin-top: -8px;
  margin-bottom: 10px;
}

/* allow table cells to wrap text and grow */
.auto-height-cell .el-table__cell {
  white-space: normal;
  word-break: break-word;
  padding: 8px;
}

/* make the textarea itself auto-height */
.auto-height-input ::v-deep .el-textarea__inner {
  line-height: 1.4;
  resize: none;          /* disable manual resize handle */
  overflow: hidden;      /* let autosize do its work */
}

</style>
